import type { ListPaginationInterface } from '@/interfaces/base/pagination/listPagination.interface';
import { PIPELINE_DEAL_SEARCH_PAGE_SIZE } from '@/modules/pipeline/constants/Pipeline.constants';
import { clonePipelineDealsFilters } from '@/modules/pipeline/schemas/PipelineDealsFilters.schema';
import type {
  PipelineBoardCacheEntry,
  PipelineBoardCacheEntryInput,
  PipelineBoardTableRowView,
  PipelineBoardTableState,
  PipelineDealCardViewRecord,
  PipelineKanbanColumnState,
} from '@/modules/pipeline/types/Pipeline.types';
import {
  buildPipelineKanbanColumnsState,
  normalizePipelineKanbanColumnState,
} from '@/modules/pipeline/utils/PipelinePresentation.utils';

export const cloneDealCards = (
  cards: Array<PipelineDealCardViewRecord>,
): Array<PipelineDealCardViewRecord> => {
  return cards.map((card) => ({ ...card }));
};

export const cloneColumnsState = (
  columns: Array<PipelineKanbanColumnState>,
): Array<PipelineKanbanColumnState> => {
  return columns.map((column) => ({
    ...column,
    cards: cloneDealCards(column.cards),
  }));
};

export const cloneTableRows = (
  rows: Array<PipelineBoardTableRowView>,
): Array<PipelineBoardTableRowView> => {
  return rows.map((row) => ({ ...row }));
};

export const getDefaultTablePagination = (): ListPaginationInterface => ({
  page: 1,
  totalItems: 0,
  totalPages: 0,
});

export const getDefaultTableState = (): PipelineBoardTableState => ({
  rows: [],
  pagination: getDefaultTablePagination(),
  isLoading: false,
});

export const cloneTableState = (
  tableState: PipelineBoardTableState,
): PipelineBoardTableState => {
  return {
    ...tableState,
    rows: cloneTableRows(tableState.rows),
    pagination: {
      ...tableState.pagination,
    },
  };
};

export const deriveStageIds = (
  columns: Array<PipelineKanbanColumnState>,
): string[] => {
  return columns.map((column) => column.id);
};

const normalizeLoadedPages = (pages?: Array<number>): Array<number> => {
  return [...new Set(pages?.length ? pages : [1])].sort((left, right) => {
    return left - right;
  });
};

const normalizeLoadedPagesByStageId = (
  columns: Array<PipelineKanbanColumnState>,
  loadedPagesByStageId?: Record<string, Array<number>>,
): Record<string, Array<number>> => {
  return Object.fromEntries(
    columns.map((column) => [
      column.id,
      normalizeLoadedPages(loadedPagesByStageId?.[column.id]),
    ]),
  );
};

const normalizeHasMoreByStageId = (
  columns: Array<PipelineKanbanColumnState>,
  hasMoreByStageId?: Record<string, boolean>,
): Record<string, boolean> => {
  return Object.fromEntries(
    columns.map((column) => [
      column.id,
      hasMoreByStageId?.[column.id] ?? column.hasMore,
    ]),
  );
};

const normalizeColumns = (
  input: PipelineBoardCacheEntryInput,
): Array<PipelineKanbanColumnState> => {
  if (input.columns) {
    return input.columns.map(normalizePipelineKanbanColumnState);
  }

  return buildPipelineKanbanColumnsState(input.response);
};

const normalizeTableState = (
  tableState?: PipelineBoardTableState,
): PipelineBoardTableState => {
  if (!tableState) {
    return getDefaultTableState();
  }

  return {
    rows: cloneTableRows(tableState.rows),
    pagination: {
      ...tableState.pagination,
    },
    isLoading: tableState.isLoading,
  };
};

export const normalizeBoardEntry = (
  input: PipelineBoardCacheEntryInput,
): PipelineBoardCacheEntry => {
  const columns = cloneColumnsState(normalizeColumns(input));
  const stageIds = deriveStageIds(columns);

  return {
    fetchedAt: input.fetchedAt ?? Date.now(),
    response: input.response,
    mode: input.mode,
    filters: clonePipelineDealsFilters(input.filters),
    filterKey: input.filterKey,
    columns,
    stageIds,
    tableState: normalizeTableState(input.tableState),
    loadedPagesByStageId: normalizeLoadedPagesByStageId(
      columns,
      input.loadedPagesByStageId,
    ),
    pageSize: input.pageSize ?? PIPELINE_DEAL_SEARCH_PAGE_SIZE,
    hasMoreByStageId: normalizeHasMoreByStageId(
      columns,
      input.hasMoreByStageId,
    ),
  };
};
